/**
 * What's New sheet — INVENTORY §2 row 3 / §4.10; prototype lines 922-988.
 *
 * The queue is a snapshot taken when the sheet opens (`ui.openWhatsNew`), not a
 * live derivation: `newForMe` drops an entry the instant its vote lands, so a
 * live queue would renumber itself mid-review. The sheet only advances `wnIdx`.
 *
 * Store actions used here (`wnMark` / `wnSkip` / `wnClearAll` / `closeWhatsNew`)
 * were added to `store/slices/ui.ts` by this lane — the placeholder's ⚠ STORE
 * GAP note is resolved; see docs/porting/P3c.md (P3c-04).
 *
 * The card's dead `listType=search` embed is now `components/MediaCarousel.tsx`
 * (PORTING-NOTES P10-01), which also owns the `SEARCH YOUTUBE ↗` fallback link
 * this card used to render beneath it. The carousel calls `ensureMedia` itself,
 * which is what this surface needs: What's New never runs `openGame`, so nothing
 * else here would ever fetch the Steam trailers and screenshots.
 */

import { Avatar } from '../components/Avatar.tsx';
import { CallColumns } from '../components/CallColumns.tsx';
import { MediaCarousel } from '../components/MediaCarousel.tsx';
import { NumbersBand } from '../components/NumbersBand.tsx';
import { SteamChip } from '../components/SteamChip.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { VoteButtons } from '../components/VoteButtons.tsx';
import { useArt } from '../components/useArt.ts';
import type { GameId } from '../lib/types.ts';
import { useQd } from '../store/index.ts';
import { whatsNewVM } from '../store/selectors.ts';

type WnVM = ReturnType<typeof whatsNewVM>;
/** The populated branch of `whatsNewVM` — `hasCard` is a plain boolean, so the
 *  union needs a structural guard rather than a discriminant. */
type WnCard = Extract<WnVM, { currentId: GameId }>;
const isCard = (v: WnVM): v is WnCard => 'currentId' in v;

export default function WhatsNewSheet(): JSX.Element {
  const s = useQd();
  const vm = whatsNewVM(s);
  const card: WnCard | null = isCard(vm) ? vm : null;
  const art = useArt((card ? card.currentId : '') + 'p', card ? card.coverCands : []);

  const clearAll = (
    <div
      onClick={() => void s.wnClearAll()}
      style={{
        fontFamily: 'var(--gt-font-mono)',
        fontSize: 9,
        color: 'var(--gt-text-5)',
        border: '1px solid var(--gt-border-2)',
        padding: 8,
        cursor: 'pointer',
      }}
    >
      CLEAR ALL
    </div>
  );

  if (!card) {
    // The queue emptied under us (a realtime patch, or another tab). Render the
    // chrome only; the ◆ button is what re-opens with a fresh queue.
    return (
      <Sheet
        label="What's new sheet"
        maxHeight="90vh"
        panelStyle={{ paddingBottom: 28 }}
        hs
        onClose={() => s.closeWhatsNew()}
        header={{ title: vm.hdrTxt, titleClr: vm.hdrClr, titleFs: '8.5px', note: '0 MORE', ...(vm.showClear ? { extra: clearAll } : {}) }}
      >
        <div
          style={{
            padding: '30px 16px',
            textAlign: 'center',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 11,
            color: 'var(--gt-muted)',
          }}
        >
          {vm.emptyTxt}
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      label="What's new sheet"
      maxHeight="90vh"
      // 28px of tail so the last band clears the phone's drag/home indicator
      // (2026-08-25). Upstream put this on the What's New and filter sheets
      // only, not on every sheet the app has — so neither does this.
      panelStyle={{ paddingBottom: 28 }}
      hs
      onClose={() => s.closeWhatsNew()}
      header={{
        title: card.hdrTxt,
        titleClr: card.hdrClr,
        titleFs: '8.5px',
        note: card.progressTxt,
        // A call run has nothing to mark reviewed, so the control is absent
        // rather than disabled — there is no state in which it would apply.
        ...(card.showClear ? { extra: clearAll } : {}),
      }}
    >
      {/* BAND 1 · MEDIA — edge to edge, flush to the sheet's sides --------- */}
      <MediaCarousel gameId={card.currentId} noFoot />

      {/* BAND 2 · THE GAME ------------------------------------------------ */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 13, padding: '13px 14px 14px', background: 'var(--gt-card)' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          {/* The 58px cover is hand-inlined in the prototype, not the Cover Art
              component — no checker texture, initials at 9px. */}
          <div
            style={{
              position: 'relative',
              width: 58,
              aspectRatio: '2/3',
              alignSelf: 'flex-start',
              background: card.fall,
              overflow: 'hidden',
              border: '1px solid var(--gt-border)',
              flex: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: 9,
                color: 'rgba(255,255,255,.25)',
              }}
            >
              {card.ini}
            </div>
            {art.hasCover ? <div style={{ position: 'absolute', inset: 0, background: art.cover }} /> : null}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* The byline row anchors the STEAM chip (2026-08-25) and must not
                wrap — see the desktop twin. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: 'var(--gt-font-mono)',
                fontSize: 9,
                color: 'var(--gt-text-5)',
              }}
            >
              <Avatar
                style={{ flex: 'none' }}
                size={18}
                fs={6}
                ini={card.byIni}
                bg={card.byHueBg}
                hasAv={card.byHasAv}
                avBg={card.byAvBg}
              />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                ADDED BY {card.byTxt} · {card.agoTxt}
              </span>
              <div style={{ flex: 1 }} />
              <SteamChip href={card.steamUrl} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2, color: '#fff' }}>{card.name}</div>
            <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 9.5, color: 'var(--gt-text-5)' }}>{card.dev}</div>
            {/* The meta line and the review chip moved into THE NUMBERS on
                2026-08-24 — see the desktop twin. */}
          </div>
        </div>

        <div style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--gt-text-3)', textWrap: 'pretty' }}>{card.descTxt}</div>
      </div>

      {/* BAND 3 · YOUR CALL — above the fold ------------------------------- */}
      {/* This band was BELOW the numbers for a day, and on a 430 sheet that put
          the vote strip — the point of the sheet — behind a scroll (Marcus, on
          staging, 2026-08-25). The numbers and the crew read as supporting
          detail underneath; the call does not. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 11,
          padding: 14,
          background: 'var(--gt-raised)',
          borderTop: '1px solid var(--gt-line)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minHeight: 26 }}>
          <span
            style={{
              fontFamily: 'var(--gt-font-pixel)',
              fontSize: 7.5,
              color: 'var(--acc)',
              transform: 'translateY(0.13em)',
            }}
          >
            YOUR CALL
          </span>
          {/* The only words the crew's calls get now. */}
          <span
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 9,
              letterSpacing: '.05em',
              color: 'var(--gt-muted-2)',
            }}
          >
            {card.calledTxt}
          </span>
          <div style={{ flex: 1 }} />
          <span
            onClick={() => void s.wnSkip()}
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 10,
              color: 'var(--gt-text-5)',
              cursor: 'pointer',
              padding: '8px 9px',
            }}
          >
            SKIP →
          </span>
        </div>
        {/* No `value`: `wnMark` advances the queue, so the card is gone before a
            selection could be painted (upstream passes `on-pick` alone). */}
        <VoteButtons style={{ width: '100%' }} onPick={(l) => void s.wnMark(l)} />
        <CallColumns cols={card.callCols} />
      </div>

      {/* BAND 4 · THE NUMBERS — folded 2×2 in phone width ------------------ */}
      <NumbersBand nums={card.nums} fold />

    </Sheet>
  );
}
